<template>
    <ValidationErrors :show="showErrors"/>
    <Head :title="updating ? form.title : 'Create New Fixture'"/>
    <Audit v-if="openModal" @close="openModal = false" :open="openModal" :activity_logs="fixture.activity_logs" />
    <div class="w-full mb-6">
        <div v-if="updating === false">
            <h1 class="my-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                <span v-html="'Create New Fixture'"></span>
            </h1>
            <FixtureTabs/>
        </div>
        <fixture-header v-else :fixture="fixture">
            <template v-slot:actions>
                <Link @click="archive"
                        class="inline-flex h-10 items-center px-4 py-2 bg-gray-800 border border-transparent rounded-md font-semibold text-xs text-white uppercase bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
                    <archive-icon class="-ml-0.5 mr-2 h-4 w-4"></archive-icon>
                    Archive Fixture
                </Link>
            </template>
        </fixture-header>
        <div class="space-y-6 lg:col-span-9">
            <section aria-labelledby="payment-details-heading">
                <form @submit.prevent="submit">
                    <div class="shadow sm:rounded-md sm:overflow-hidden">
                        <div class="bg-white px-4 p-6 space-y-6">
                            <!-- Fixture Form Start -->
                            <div id="fixture_form" class="space-y-6">
                                <div class="border-b border-gray-200 pb-2">
                                    <h2 id="payment-details-heading" class="text-lg leading-6 font-bold text-gray-900">Fixture Information</h2>
                                    <p class="mt-1 text-sm text-gray-500">Enter information for the fixture below.</p>

                                    <div class="rounded-md bg-blue-50 p-4 mt-2" v-if="!updating">
                                        <div class="flex">
                                            <div class="flex-shrink-0">
                                                <InformationCircleIcon class="h-5 w-5 text-blue-400" aria-hidden="true"/>
                                            </div>
                                            <div class="ml-3 flex-1 md:flex md:justify-between">
                                                <p class="text-sm text-blue-700">Information entered here will be used
                                                    to calculate service costs and provide quick access to available
                                                    suppliers.</p>
                                            </div>
                                        </div>
                                    </div>
                                    <p class="mt-2 w-full text-right text-xs text-gray-500 font-italic">Fields marked with an asterisk (<span class="text-red-500">*</span>) are required.</p>
                                </div>

                                <div class="grid grid-cols-4 gap-6">
                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="title"
                                               class="block text-sm font-medium text-gray-700 required">Fixture / Event</label>
                                        <input type="text" v-model="form.title" name="title" id="title"
                                               autocomplete="name"
                                               :class="{'border-red-500': form.errors.title}"
                                               class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="short_title"
                                               class="block text-sm font-medium text-gray-700 required">Short Name (Opponent or Tour Name, max 16 chars)</label>
                                        <input type="text" v-model="form.short_title" name="short_title" id="short_title"
                                               maxlength="16"
                                               :class="{'border-red-500': form.errors.short_title}"
                                               class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="sm:hidden md:block md:col-span-2"></div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="trading_details_id"
                                               class="block text-sm font-medium text-gray-700 required">
                                            Trading Name
                                        </label>
                                        <v-select class="mt-1" :class="{'border-red-500': form.errors.trading_details_id}" :options="trading_details" label="trading_name" :reduce="trading_details => trading_details.id" v-model="form.trading_details_id" :disabled="overview_status === 'Invoicing' || overview_status === 'Quoting'" />
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-2">
                                        <label for="client_id"
                                               class="block text-sm font-medium text-gray-700 required">Client</label>
                                        <v-select class="mt-1 font-small" :class="{'border-red-500': form.errors.client_id}" :options="filteredClients" label="trading_name" :reduce="filteredClients => filteredClients.id" v-model="form.client_id" />
                                    </div>

                                    <div class="sm:hidden md:block md:col-span-1"></div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label
                                            class="block text-sm font-medium text-gray-700 required">Fixture
                                            Type</label>
                                            <v-select class="mt-1" :class="{'border-red-500': form.errors.fixture_type_id}" :options="fixture_types" label="name" :reduce="fixture_types => fixture_types.id" v-model="form.fixture_type_id" />
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="home_away"
                                               class="block text-sm font-medium text-gray-700 required">Home or Away</label>
                                        <select id="home_away" v-model="form.home_away" class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                            <option value="A" selected>Away</option>
                                            <option value="H">Home</option>
                                        </select>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="passengers"
                                               class="block text-sm font-medium text-gray-700">Passengers</label>
                                        <input v-model="form.passengers" type="number"
                                               name="passengers" id="passengers"
                                               autocomplete="passengers"
                                               :class="{'border-red-500': form.errors.passengers}"
                                               class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                    <label for="starts_at" class="block text-sm font-medium text-gray-700">Fixture Date <span v-if="formattedStartDate">({{ formattedStartDate }})</span></label>
                                    <input v-model="form.starts_at" type="date" name="starts_at" id="starts_at" autocomplete="starts_at"
                                        :class="{'border-red-500': form.errors.starts_at}"
                                        class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                                        @change="handleDateChange"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                    <label for="ends_at" class="block text-sm font-medium text-gray-700">End Date <span v-if="formattedEndDate">({{ formattedEndDate }})</span></label>
                                    <input v-model="form.ends_at" type="date" name="ends_at" id="ends_at" autocomplete="ends_at"
                                        :class="{'border-red-500': form.errors.ends_at}"
                                        class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="enquiry_at"
                                               class="block text-sm font-medium text-gray-700 required">Enquiry
                                            Date <span v-if="formattedEnquiryDate">({{ formattedEnquiryDate }})</span></label>
                                        <input v-model="form.enquiry_at" type="date"
                                               name="enquiry_at" id="enquiry_at"
                                               autocomplete="enquiry_at"
                                               :class="{'border-red-500': form.errors.enquiry_at}"
                                               class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>

                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label
                                            class="block text-sm font-medium text-gray-700 required">Country</label>
                                        <v-select class="h-full mt-1" :class="{'border-red-500': form.errors.country_id}" :options="countries" label="name" :reduce="countries => countries.id" v-model="form.country_id" />
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label
                                            class="block text-sm font-medium text-gray-700 required">Venue/Location</label>
                                        <v-select class="h-full mt-1" :class="{'border-red-500': form.errors.region_id}" :options="regions" label="name" :reduce="regions => regions.id" v-model="form.region_id" />
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label class="block text-sm font-medium text-gray-700 required">Team Manager</label>
                                        <v-select class="h-full mt-1" :class="{'border-red-500': form.errors.user_id}" :options="users" label="name" :reduce="users => users.id" v-model="form.user_id" />
                                    </div>

                                    <div class="col-span-8 sm:col-span-4 lg:col-span-2">
                                        <label
                                            class="block text-sm font-medium text-gray-700">Additional Team
                                            Managers</label>
                                         <v-select class="mt-1"
                                            :class="{'border-red-500': form.errors.atm_ids}"
                                            :options="users" label="name" :reduce="(users) => users.id"
                                            v-model="form.atm_ids" taggable multiple/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label
                                            class="block text-sm font-medium text-gray-700 required">Commission
                                            Type</label>
                                        <select id="fixture_type_id" v-model="form.commission_type"
                                                :class="{'border-red-500': form.errors.commission_type}"
                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                            <option value selected v-html="'Select a commission type'"></option>
                                            <option v-for="commission_type in commission_types" :value="commission_type"
                                                    v-html="commission_type.charAt(0).toUpperCase() + commission_type.substring(1)"></option>
                                        </select>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label
                                            class="block text-sm font-medium text-gray-700 required">Estimated
                                            Cost</label>
                                        <input v-model="form.estimated_cost" type="text"
                                               name="estimated_cost" id="estimated_cost"
                                               autocomplete="estimated_cost"
                                               :class="{'border-red-500': form.errors.estimated_cost}"
                                               class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>

                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label
                                            class="block text-sm font-medium text-gray-700 required">Estimated
                                            Revenue</label>
                                        <input v-model="form.estimated_price" type="text"
                                               name="estimated_price" id="estimated_price"
                                               autocomplete="estimated_price"
                                               :class="{'border-red-500': form.errors.estimated_price}"
                                               class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>

                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label
                                            class="block text-sm font-medium text-gray-700 required">Status</label>
                                        <select id="fixture_type_id" v-model="form.status"
                                                :class="{'border-red-500': form.errors.status}"
                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                            <option value selected v-html="'Select a project status'"></option>
                                            <option v-for="status in fixture_statuses" :value="status"
                                                    v-html="status.charAt(0).toUpperCase() + status.substring(1)"></option>
                                        </select>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label
                                            class="block text-sm font-medium text-gray-700 required">Currency Being Invoiced</label>
                                        <v-select class="h-full mt-1" :class="{'border-red-500': form.errors.currency_id}" :options="currencies" label="name" :reduce="currencies => currencies.id" v-model="form.currency_id" />
                                    </div>

                                    <div class="col-span-4 clear-both">
                                        <label
                                            class="block text-sm font-medium text-gray-700">Description</label>
                                        <div class="mt-1">
                                            <quill-editor content-type="html" v-model:content="form.description"
                                                          :class="{'border-red-500': form.errors.description}"
                                                          class="min-h-[150px] block w-full bg-white border border-gray-300 shadow-sm py-2 px-3"
                                                          theme="snow"></quill-editor>
                                        </div>
                                    </div>

                                    <div class="col-span-4 clear-both">
                                        <label
                                            class="block text-sm font-medium text-gray-700">Notes - Internal Use Only</label>
                                        <div class="mt-1">
                                            <quill-editor content-type="html" v-model:content="form.notes"
                                                          :class="{'border-red-500': form.errors.notes}"
                                                          class="min-h-[150px] mt-1 block w-full bg-white border border-gray-300 shadow-sm py-2 px-3"
                                                          theme="snow"></quill-editor>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                        <div class="px-4 py-3 bg-gray-50 text-right sm:px-6">
                            <button type="submit"
                                    class="bg-pitch-purple border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-700">
                                <span v-if="updating === true">Update</span>
                                <span v-else>Save</span>
                            </button>
                        </div>
                    </div>
                </form>
            </section>
        </div>
    </div>
</template>

<script>
import Authenticated from "@/Layouts/Authenticated";
import {ref} from "vue";
import {Link, Head, usePage, useForm} from "@inertiajs/vue3";
import {ClipboardCopyIcon, PlusSmIcon, UsersIcon, InformationCircleIcon} from "@heroicons/vue/outline"
import ValidationErrors from "@/Components/ValidationErrors";
import FixtureTabs from "@/Components/FixtureTabs";
import FixtureHeader from "@/Components/FixtureHeader";
import {ArchiveIcon} from "@heroicons/vue/outline";
import { router } from '@inertiajs/vue3'
import Button from "@/Components/Button";
import moment from "moment";
import Audit from "@/Components/Modal/Audit.vue";

export default {
    layout: Authenticated,
    components: {
        FixtureHeader,
        FixtureTabs,
        ValidationErrors,
        Link,
        Head,
        ClipboardCopyIcon,
        PlusSmIcon,
        UsersIcon,
        InformationCircleIcon,
        ArchiveIcon,
        Button,
        Audit
    },
    setup(props) {

        const fixture = usePage().props.fixture ?? null;
        const updating = usePage().props.updating
        const showErrors = ref(false);
        const currencies = usePage().props.currencies;

        // Used for audit activity logs modal
        const openModal = ref(false);

        const form = useForm({
            id: updating === true ? fixture.id : null,
            title: updating === true ? fixture.title : null,
            short_title: updating === true ? fixture.short_title : null,
            home_away: updating === true ? fixture.home_away : "A",
            client_id: updating === true ? fixture.client_id : "",
            fixture_type_id: updating === true ? fixture.fixture_type_id : "",
            passengers: updating === true ? fixture.passengers : null,
            starts_at: updating === true ? fixture.starts_at : null,
            ends_at: updating === true ? fixture.ends_at : null,
            enquiry_at: updating === true ? fixture.enquiry_at : moment().format('YYYY-MM-DD'), // default to today if not specified
            country_id: updating === true ? fixture.country_id : "",
            region_id: updating === true ? fixture.region_id : "",
            user_id: updating === true ? fixture.user_id : usePage().props.auth.user.id,
            commission_type: updating === true ? fixture.commission_type : "",
            estimated_price: updating === true ? fixture.estimated_price : null,
            estimated_cost: updating === true ? fixture.estimated_cost : null,
            status: updating === true ? fixture.status : "Request",
            notes: updating === true ? fixture.notes : "",
            description: updating === true ? fixture.description : "",
            trading_details_id: updating === true ? fixture.trading_details_id : "",
            currency_id: updating === true ? fixture.currency_id : "",
            atm_ids: updating === true ? fixture.additionalteammanagers.map(t => t.id) : [],
        });

        // Fixture Overview Status
        const overview_status = usePage().props.overview_status;

        // Fixture Types
        const fixture_types = usePage().props.fixture_types;

        // Countries
        const countries = usePage().props.countries;

        // Regions
        const regions = (updating === true) ? countries.find(country => country.id === form.country_id).regions : [];

        // Trading Details
        const trading_details = usePage().props.trading_details;

        // Filtered clients list
        const filteredClients = (updating === true) ? usePage().props.clients : usePage().props.filtered_clients;

        // Clients
        const clients = usePage().props.clients;

        // Users
        const users = usePage().props.users;

        // Commission Types
        const commission_types = usePage().props.commission_types;

        // Fixture Statuses
        const fixture_statuses = usePage().props.fixture_statuses;

        return {
            form,
            countries,
            regions,
            fixture_types,
            filteredClients,
            clients,
            updating,
            users,
            commission_types,
            fixture_statuses,
            overview_status,
            showErrors,
            fixture,
            trading_details,
            currencies,
            openModal
        }

    },
    computed: {
        statuses() {
            const formattedStatuses = [];

            // Iterate over the keys and values of the statuses object
            for (const key in this.fixture_statuses) {
                if (Object.hasOwnProperty.call(this.fixture_statuses, key)) {
                    const id = key;
                    const status = this.fixture_statuses[key];

                    formattedStatuses.push({ id, status });
                }
            }

            return formattedStatuses;
        },
        // commission_types() {
        //     const formattedCommissionTypes = [];

        //     // Iterate over the keys and values of the commission_types object
        //     for (const key in this.commission_types) {
        //         if (Object.hasOwnProperty.call(this.commission_types, key)) {
        //             const id = key;
        //             let commission_type = this.commission_types[key];

        //             formattedCommissionTypes.push({ id, commission_type });
        //         }
        //     }

        //     return formattedCommissionTypes;
        // },
        formattedStartDate() {
            if (this.form.starts_at) {
                return moment(this.form.starts_at).format('DD MMM YYYY');
            }
            return '';
        },
        formattedEndDate() {
            if (this.form.ends_at) {
                return moment(this.form.ends_at).format('DD MMM YYYY');
            }
            return '';
        },
        formattedEnquiryDate() {
            if (this.form.ends_at) {
                return moment(this.form.enquiry_at).format('DD MMM YYYY');
            }
            return '';
        },
    },
    methods: {
        /**
         * Submit the form and perform validation on the request.
         */
         submit() {
            const postOptions = {
                onError: (errors) => { this.showErrors = true; },
                resetOnSuccess: true,
                preserveState: (page) => Object.keys(page.props.errors).length
            };

            if (this.updating) {
                this.form.post(route('frontend.fixture.update', [this.form.id]), { ...postOptions, preserveScroll: false });
                return;
            }

            const postForm = () => this.form.post(route('frontend.fixture.store'), postOptions);
            const currentDate = moment();

            const isEndDatePassed = moment(this.form.ends_at).isBefore(currentDate);
            const isStartDatePassed = moment(this.form.starts_at).isBefore(currentDate);

            if (isEndDatePassed || isStartDatePassed) {
                const errorMessage = isEndDatePassed
                    ? `The fixture you are trying to create has an end date that's already passed (${this.formattedEndDate}).`
                    : `The fixture you are trying to create has a start date that's already passed (${this.formattedStartDate}).`;

                this.$swal.fire({
                    title: 'Create a New Fixture',
                    html: `${errorMessage} Would you like to continue with creating the fixture?`,
                    showCloseButton: true,
                    showCancelButton: true,
                    confirmButtonText: 'Yes',
                    cancelButtonText: 'No'
                }).then(result => result.isConfirmed && postForm());
            } else {
                postForm();
            }
        },
        archive() {
            this.$swal.fire({
                title: 'Archive Fixture',
                html: "You are archiving a fixture. <br/>Are you sure you'd like to do this?",
                showCloseButton: true,
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'No'
            })
                .then((result) => {
                    if (result.isConfirmed) {
                        // Delete the country
                        this.$inertia.delete(route('frontend.fixture.archive', this.form.id), {
                            preserveScroll: true
                        });
                    }
                });
        }
    },
    watch: {
        'form.country_id': {
            deep: true,
            handler(id) {
                if (id === "" || (this.countries.find(country => country.id === id) === "undefined" && typeof this.countries.find(country => country.id === id).regions === "undefined")) {
                    this.regions = [];
                } else {
                    this.regions = this.countries.find(country => country.id === id)?.regions
                }
            }
        },
        'form.starts_at'(newValue) {
            this.form.ends_at = newValue;
        },
        'form.trading_details_id'(newId) {
            // Check for null new ID
            if(newId == null) {
                this.form.client_id = null;
                this.form.currency_id = null;
                this.filteredClients = [];
                return;
            }

            // Find the trading detail record with the matching ID
            let tradingDetail = this.trading_details.find(detail => detail.id == newId);

            // Instantiate IDs array
            let ids = [];

            // Set the client check ID
            if (tradingDetail) {
                // Check for stand alone entity
                if (tradingDetail.stand_alone_entity == 0) {
                    tradingDetail = this.trading_details.find(detail => detail.id == tradingDetail.related_trading_entity_id);
                }

                // Set the IDS
                ids = [tradingDetail.id, ...tradingDetail.related_trading_details.map(item => item.id)];

                // Set the currency ID to that of the trading detail
                this.form.currency_id = tradingDetail.currency_id;
            }

            // Filter the client list
            this.filteredClients = this.clients.filter(client => ids.includes(client.trading_details_id));
        }
    }
}
</script>
